import { useEffect, useState } from "react";
import api from "../api/axios";
import { Link } from "react-router-dom";
import AlertBox from "../components/AlertBox";
import { useTranslation } from "react-i18next";

export default function OrdersPage() {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/orders")
            .then((res) => {
                const payload = res.data.data ?? res.data;
                setOrders(payload.data ?? payload);
            })
            .catch((e) => {
                setErr(e.response?.data?.message || t("orders_load_error"));
            })
            .finally(() => setLoading(false));
    }, [t]);

    const getStatusInfo = (status) => {
        switch (status?.toLowerCase()) {
            case "paid":
                return { color: "success", icon: "bi-check-circle-fill", label: t("status_paid") };
            case "pending":
                return { color: "warning", icon: "bi-clock-history", label: t("status_pending") };
            case "shipped":
                return { color: "primary", icon: "bi-truck", label: t("status_shipped") };
            case "cancelled":
                return { color: "danger", icon: "bi-x-circle-fill", label: t("status_cancelled") };
            default:
                return { color: "secondary", icon: "bi-info-circle-fill", label: status || t("status_unknown") };
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center my-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status" />
                <p className="mt-3 text-muted fw-bold">{t("loading_your_orders")}</p>
            </div>
        );
    }

    return (
        <div className="container mb-5 py-4">
            <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3">
                <h2 className="m-0 fw-bold"><i className="bi bi-box-seam me-3 text-primary"></i>{t("my_orders_title")}</h2>
                <Link to="/products" className="btn btn-outline-primary">
                    <i className="bi bi-cart me-2"></i>{t("continue_shopping")}
                </Link>
            </div>

            {err && <AlertBox type="danger" message={err} onClose={() => setErr("")} />}

            {orders.length === 0 && !err ? (
                <div className="alert alert-info text-center py-5 shadow-sm rounded">
                    <i className="bi bi-inbox display-1 d-block mb-3 text-muted"></i>
                    <h5 className="mb-3">{t("no_orders_yet")}</h5>
                    <Link to="/products" className="btn btn-primary px-4">{t("start_shopping_now")}</Link>
                </div>
            ) : (
                <div className="row g-4">
                    {orders.map((o) => {
                        const statusInfo = getStatusInfo(o.status);

                        return (
                            <div className="col-lg-6" key={o.id}>
                                <div className="card shadow-sm border-0 h-100 transition-all hover-shadow">
                                    <div className="row g-0 h-100">
                                        <div className="col-sm-3 d-flex align-items-center justify-content-center bg-light border-end">
                                            <i className="bi bi-box2-heart display-3 text-secondary opacity-50"></i>
                                        </div>

                                        <div className="col-sm-9 d-flex flex-column">
                                            <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                                                <div className="small d-flex flex-wrap gap-2">
                                                    <div>
                                                        <span className="border bg-secondary rounded-start px-2 text-white">{t("order_id_label")}</span>
                                                        <span className="border bg-white rounded-end px-2 fw-bold">#{o.id}</span>
                                                    </div>
                                                    <div>
                                                        <span className="border bg-secondary rounded-start px-2 text-white">{t("date_label")}</span>
                                                        <span className="border bg-white rounded-end px-2">{o.created_at?.slice(0, 10)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-body">
                                                <h5 className="fw-bold mb-3 text-dark">
                                                    {t("order_total_label")} <span className="text-success">${o.total}</span>
                                                </h5>

                                                <Link to={`/orders/${o.id}`} className="btn btn-sm btn-outline-dark px-3">
                                                    <i className="bi bi-eye me-2"></i>{t("view_details")}
                                                </Link>
                                            </div>

                                            <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center mt-auto">
                                                <div>
                                                    <span className="text-muted mx-2 small">{t("order_status_label")}</span>
                                                    <span className={`text-${statusInfo.color} fw-bold`}>
                                                        <i className={`bi ${statusInfo.icon} mx-1`}></i>
                                                        {statusInfo.label}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <style>{`
                .hover-shadow:hover { transform: translateY(-3px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
            `}</style>
        </div>
    );
}